import { IClient } from '../../../../Assembler/dist/registry/iface/iClient.js';
import type { ITransientStore } from '../../../../Assembler/dist/registry/iface/iTransientStore.js';
import type { GetParentJsonValueHandler } from '../../../../Assembler/dist/registry/iface/getParentJsonValueHandler.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { AppSiteFoldersFilter } from '../../registryCommon/dynamicData/appSiteFoldersFilter.js';

/**
 * Client-side CLIENT HANDLER for the AppSite-folder selector — the "processor" half of the two-handler split.
 * Consumes the RAW folder set (produced by AppSiteFoldersClientData, the server "preparer") and
 * resolves/renders the folder options WITHOUT a server round-trip.
 *
 * - Parse the raw folder set handed IN (never enumerated/fetched here — source-agnostic)
 * - Resolve the SelectedAppSiteFolder (the client's live selection, else the delivered default), REUSING the
 *   shared AppSiteFoldersFilter resolve/build so the client tier is byte-identical to the server processData
 *
 * Distinct from AppSiteFoldersClientData (the server preparer): this is the client processor, registered on
 * the SEPARATE IClient registry. The same body runs in the browser bundle and in the console
 * --apiclient/--ociclient simulation, certifying the browser artifact by construction. Mirrors ScenarioClient.
 */
export class AppSiteFoldersClient extends IClient {
    /**
     * Gets the full type name for client-handler operation tracking.
     */
    get typeName(): string {
        return 'AppSiteFoldersClient';
    }

    /**
     * Resolves the RAW folder set into the folder-picker result: the client's live SelectedAppSiteFolder wins
     * over the delivered DefaultAppSiteFolder, REUSING the shared AppSiteFoldersData statics so the client tier
     * is byte-identical to the server processData. The STATIC skip (SkipViewAppSitesFolder) is already applied
     * by the server preparer AppSiteFoldersClientData, so the raw handed IN is skip-filtered. Source-agnostic —
     * the raw is handed in (embedded data island / remote slice).
     */
    process(appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, transientStore: ITransientStore, configJson: JsonObject | null, getParentJsonValue: GetParentJsonValueHandler | null, rawJson: JsonObject): JsonObject {
        void appSite; void appView; void appFile; void componentName; void componentDepth; void resolvedAppSite; void resolvedComponentName; void paramJson; void transientStore;
        if (rawJson === null || rawJson === undefined || !rawJson.has('AppSiteFolders') || !rawJson.get('AppSiteFolders').isArray) {
            return new JsonObject();
        }

        const folders = AppSiteFoldersFilter.parseFolders(rawJson.get('AppSiteFolders').asArray());
        if (folders.length === 0) {
            return new JsonObject();
        }

        const defaultFolder = (rawJson.has('DefaultAppSiteFolder') && rawJson.get('DefaultAppSiteFolder').isString)
            ? rawJson.get('DefaultAppSiteFolder').toString() : '';

        // Resolve the live SelectedAppSiteFolder override IDENTICALLY to the server data handler:
        // configJson > componentJson > parent via delegate (empty => fall back to the delivered default).
        let selectedOverride = '';
        if (configJson !== null && configJson.has('SelectedAppSiteFolder') && configJson.get('SelectedAppSiteFolder').isString) {
            selectedOverride = configJson.get('SelectedAppSiteFolder').toString();
        } else if (componentJson !== null && componentJson !== undefined && componentJson.has('SelectedAppSiteFolder') && componentJson.get('SelectedAppSiteFolder').isString) {
            selectedOverride = componentJson.get('SelectedAppSiteFolder').toString();
        } else if (getParentJsonValue !== null) {
            selectedOverride = getParentJsonValue('SelectedAppSiteFolder') ?? '';
        }

        const selectedFolder = AppSiteFoldersFilter.resolveSelectedFolder(folders, defaultFolder, selectedOverride);
        return AppSiteFoldersFilter.buildFoldersResult(folders, selectedFolder);
    }
}
